"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { useSession } from "@/hooks/use-session";
import { useFavorites } from "@/hooks/use-favorites";
import { Heart } from "lucide-react";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

interface FavoriteButtonProps {
  essenceId: string;
  className?: string;
  /** "icon" is a compact heart; "label" adds the word and a pill outline. */
  variant?: "icon" | "label";
  size?: "sm" | "md";
}

export function FavoriteButton({
  essenceId,
  className,
  variant = "icon",
  size = "sm",
}: FavoriteButtonProps) {
  const router = useRouter();
  const { session } = useSession();
  const { favorites, toggle, isPending } = useFavorites();
  const [pop, setPop] = useState(false);

  const isFavorite = favorites.includes(essenceId);

  const handleClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (!session) {
        toast("Sign in to save favorites", {
          action: { label: "Log in", onClick: () => router.push("/login") },
        });
        return;
      }

      const next = !isFavorite;
      if (next) {
        setPop(true);
        window.setTimeout(() => setPop(false), 350);
      }
      try {
        await toggle({ essenceId, favorite: next });
      } catch {
        toast.error("Couldn't update favorite. Try again.");
      }
    },
    [essenceId, isFavorite, router, session, toggle]
  );

  const heart = (
    <Heart
      className={cn(
        "transition-[color,fill,transform] duration-150",
        size === "sm" ? "size-[17px]" : "size-[18px]",
        isFavorite ? "fill-accent text-accent" : "text-ink-3",
        pop && "animate-pop"
      )}
      strokeWidth={isFavorite ? 2 : 1.9}
    />
  );

  if (variant === "label") {
    return (
      <button
        type="button"
        onClick={handleClick}
        disabled={isPending}
        aria-pressed={isFavorite}
        aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
        className={cn(
          "inline-flex h-9 items-center gap-2 rounded-full border px-3.5 text-[13px] font-medium transition-colors duration-150",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent disabled:opacity-60",
          isFavorite
            ? "border-[rgb(167_139_250/0.4)] bg-accent-tint text-ink hover:bg-accent-tint-strong"
            : "border-line-strong bg-transparent text-ink-2 hover:border-line-accent hover:text-ink",
          className
        )}
      >
        {heart}
        {isFavorite ? "Favorited" : "Favorite"}
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isPending}
      aria-pressed={isFavorite}
      aria-label={isFavorite ? "Remove from favorites" : "Add to favorites"}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full transition-colors duration-150",
        size === "sm" ? "size-9" : "size-10",
        "hover:bg-surface-3 focus-visible:outline-2 focus-visible:outline-accent disabled:opacity-60",
        isFavorite ? "text-accent" : "text-ink-3 hover:text-ink-2",
        className
      )}
    >
      {heart}
    </button>
  );
}
